/**
 * Phase 15.4 — Market Return Trip Log (back-haul)
 * Driver opens this from a parent trip's detail page.
 * Captures: transport contact + freight + dala + builty + amount received.
 * Backend creates a separate trip (trip_type=market_return) + market_receivables entry.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Truck, MapPin, Phone, IndianRupee, CheckCircle2, Loader2,
  Building2,
} from "lucide-react";

const inputCls =
  "w-full rounded-xl bg-white border border-slate-300 px-4 py-3 text-base font-semibold focus:outline-none focus:ring-2 focus:ring-bravura focus:border-bravura";

function FL({ children, required }) {
  return (
    <label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold block mb-1.5">
      {children} {required && <span className="text-red-500">*</span>}
    </label>
  );
}

export default function MarketReturnLog() {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const [parent, setParent] = useState(null);
  const [locations, setLocations] = useState([]);
  const [primaryTruck, setPrimaryTruck] = useState(null);
  const [busy, setBusy] = useState(false);

  const [f, setF] = useState({
    date: new Date().toISOString().slice(0, 10),
    truck_id: "",
    pickup_location_id: "",
    delivery_location_id: "",
    start_odometer: "",
    end_odometer: "",
    transport_name: "",
    transport_person_phone: "",
    freight_amount: "",
    dala_charges: "",
    builty_charges: "",
    amount_received: "",
    notes: "",
  });
  const upd = (p) => setF((x) => ({ ...x, ...p }));

  useEffect(() => {
    (async () => {
      try {
        const [p, locs, pt] = await Promise.all([
          api.get(`/logistics/trips/by-driver/${tripId}`),
          api.get("/logistics/locations/for-driver"),
          api.get("/logistics/trucks/my-primary").catch(() => ({ data: {} })),
        ]);
        setParent(p.data);
        setLocations(locs.data || []);
        const pTruck = pt.data?.truck;
        setPrimaryTruck(pTruck || null);
        // Pre-fill: same truck, delivery defaults to parent's start (Unit) if we have it
        const startId = p.data?.start_location_id;
        upd({
          truck_id: pTruck?.id || "",
          delivery_location_id: startId || "",
        });
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId]);

  const submit = async () => {
    if (!f.transport_name.trim()) {
      toast.warning("Transport name is required"); return;
    }
    if (!f.freight_amount || parseFloat(f.freight_amount) <= 0) {
      toast.warning("Freight amount required"); return;
    }
    setBusy(true);
    try {
      const payload = {
        parent_trip_id: tripId,
        date: f.date,
        truck_id: f.truck_id || primaryTruck?.id,
        pickup_location_id: f.pickup_location_id || null,
        delivery_location_id: f.delivery_location_id || null,
        start_odometer: f.start_odometer ? Number(f.start_odometer) : null,
        end_odometer: f.end_odometer ? Number(f.end_odometer) : null,
        transport_name: f.transport_name.trim(),
        transport_person_phone: f.transport_person_phone || null,
        freight_amount: Number(f.freight_amount),
        dala_charges: f.dala_charges ? Number(f.dala_charges) : 0,
        builty_charges: f.builty_charges ? Number(f.builty_charges) : 0,
        amount_received: f.amount_received ? Number(f.amount_received) : 0,
        notes: f.notes || null,
      };
      const { data } = await api.post(
        `/logistics/trips/${tripId}/market-return`, payload,
      );
      toast.success(
        `Market return logged ✓ — outstanding ₹${data.receivable.outstanding}`,
      );
      navigate(`/driver/trips/${data.trip.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const freight = parseFloat(f.freight_amount) || 0;
  const received = parseFloat(f.amount_received) || 0;
  const outstanding = (freight - received).toFixed(2);

  return (
    <div className="space-y-4" data-testid="market-return-page">
      <Link to={`/driver/trips/${tripId}`}
            className="inline-flex items-center gap-1.5 text-xs text-slate-500">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to parent trip
      </Link>

      <div className="rounded-2xl bg-gradient-to-br from-amber-500 to-red-500 text-white p-5">
        <div className="text-[10px] uppercase tracking-widest text-white/80">
          Market Return Trip (Back-haul)
        </div>
        <div className="text-2xl font-bold" style={{ fontFamily: "Chivo" }}>
          Linked to trip {parent ? `#${parent.trip_seq}` : "…"}
        </div>
        {parent && (
          <div className="text-xs text-white/90 flex items-center gap-2 mt-1">
            <Truck className="h-3.5 w-3.5" /> {parent.truck_no}
            <span>•</span>
            <MapPin className="h-3.5 w-3.5" /> {parent.location_name}
          </div>
        )}
      </div>

      <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-5 space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <FL required>Date</FL>
            <input type="date" className={inputCls} value={f.date}
                   onChange={(e) => upd({ date: e.target.value })}
                   data-testid="mr-date" />
          </div>
          <div>
            <FL>Truck</FL>
            <input className={inputCls} disabled
                   value={primaryTruck?.truck_no || parent?.truck_no || "—"} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <FL>Start odo</FL>
            <input type="number" className={inputCls} value={f.start_odometer}
                   onChange={(e) => upd({ start_odometer: e.target.value })}
                   data-testid="mr-start-odo" />
          </div>
          <div>
            <FL>End odo</FL>
            <input type="number" className={inputCls} value={f.end_odometer}
                   onChange={(e) => upd({ end_odometer: e.target.value })}
                   data-testid="mr-end-odo" />
          </div>
        </div>

        <div>
          <FL>Pickup location (market)</FL>
          <select className={inputCls} value={f.pickup_location_id}
                  onChange={(e) => upd({ pickup_location_id: e.target.value })}
                  data-testid="mr-pickup">
            <option value="">— Select —</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>{l.label || l.name}</option>
            ))}
          </select>
        </div>

        <div>
          <FL>Delivery location</FL>
          <select className={inputCls} value={f.delivery_location_id}
                  onChange={(e) => upd({ delivery_location_id: e.target.value })}
                  data-testid="mr-delivery">
            <option value="">— Select —</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.location_type === "depot" ? "🏠 " : ""}{l.label || l.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-5 space-y-3">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
          Transport / market party
        </div>
        <div>
          <FL required>Transport name</FL>
          <input className={inputCls} value={f.transport_name}
                 onChange={(e) => upd({ transport_name: e.target.value })}
                 placeholder="e.g. Shree Bala Ji Transport"
                 data-testid="mr-transport-name" />
        </div>
        <div>
          <FL>Person mobile no.</FL>
          <input type="tel" className={inputCls} value={f.transport_person_phone}
                 onChange={(e) => upd({ transport_person_phone: e.target.value })}
                 placeholder="+91…"
                 data-testid="mr-transport-phone" />
        </div>
      </div>

      <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-5 space-y-3">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
          <IndianRupee className="h-3 w-3" /> Freight settlement
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Money k="freight_amount" label="Freight final ₹" f={f} upd={upd} testId="mr-freight" required />
          <Money k="dala_charges" label="Dala ₹" f={f} upd={upd} testId="mr-dala" />
          <Money k="builty_charges" label="Builty ₹" f={f} upd={upd} testId="mr-builty" />
          <Money k="amount_received" label="Amount received ₹" f={f} upd={upd} testId="mr-received" />
        </div>

        <div className="rounded-xl bg-slate-900 text-white p-3 grid grid-cols-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400">Outstanding</div>
            <div className={`text-2xl font-bold ${outstanding > 0 ? "text-amber-300" : "text-emerald-300"}`}
                 style={{ fontFamily: "Chivo" }}
                 data-testid="mr-outstanding">
              ₹ {outstanding}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-slate-400">Goes to</div>
            <div className="text-xs text-slate-200 mt-1">
              Market Receivables Ledger (company chases this).
            </div>
          </div>
        </div>
      </div>

      <button onClick={submit} disabled={busy}
              data-testid="mr-submit"
              className="w-full rounded-xl bg-emerald-600 text-white py-3.5 font-semibold flex items-center justify-center gap-2 active:scale-[0.99] shadow-md disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
        Log Market Return
      </button>
    </div>
  );
}

function Money({ k, label, f, upd, testId, required }) {
  return (
    <div>
      <FL required={required}>{label}</FL>
      <input type="number" inputMode="decimal" className={inputCls}
             value={f[k]} onChange={(e) => upd({ [k]: e.target.value })}
             data-testid={testId} />
    </div>
  );
}
